import React, { useState, ChangeEvent, FormEvent, useContext } from "react";
import styles from "./ContactUsPage.module.css";
import qrChat from "../../assets/media/company/qr-chat.png";
import qrFollow from "../../assets/media/company/qr-follow.png";
import Spinner from "../../components/Spinner/Spinner";
import { sendEmail } from "../../api/email";
import Publication from "../../components/Publication/Publication";
import { imgOffice } from "../../assets/assetsExport";
import { useSelector } from "react-redux";
import { getLang } from "../../features/app/appSlice";
import SchemaMarkup from "../../components/SchemaMarkup/SchemaMarkup";
import { motion, AnimatePresence } from "framer-motion";
import NewsHighlightSection from "../../components/News/NewsHighlightSection";

export default function ContactUsPage() {
  const lang = useSelector(getLang);

  const contactPageSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: "Contact Us | NVIG",
    url: "https://www.newvgroup.com/contact",
    description:
      "Get in touch with New Vision International Group. Visit our London headquarters at 16 Berkeley Street, Mayfair, or contact us by phone or email.",
    mainEntity: {
      "@type": "Organization",
      name: "New Vision International Group",
      alternateName: "NVIG",
      url: "https://www.newvgroup.com",
      logo: "https://www.newvgroup.com/logo192.png",
      address: {
        "@type": "PostalAddress",
        streetAddress: "16 Berkeley Street, Mayfair",
        addressLocality: "London",
        postalCode: "W1J 8DZ",
        addressCountry: "GB",
      },
      contactPoint: {
        "@type": "ContactPoint",
        telephone: "+44-20-3907-4025",
        contactType: "customer service",
        email: "info@newvgroup.com",
      },
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://www.newvgroup.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Contact Us",
        item: "https://www.newvgroup.com/contact",
      },
    ],
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqData.map((item) => ({
      "@type": "Question",
      name: item.question.en,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer.en,
      },
    })),
  };

  return (
    <div className={`${styles.contactUsPageContainer} mt-40 content-container`}>
      <SchemaMarkup data={[contactPageSchema, breadcrumbSchema, faqSchema]} />
      <p className="tag">{lang ? "FIND US" : "找到我们"}</p>
      <h1 className="font-poster text-5xl sm:text-6xl mt-6 border-b border-black pb-6 leading-tight text-secondary-dark">
        {lang ? "Contact Us" : "联系我们"}
      </h1>
      <div className="bg-secondary-light mt-10 mb-24 flex flex-col sm:flex-row justify-between gap-0 overflow-hidden">
        <div className="order-2 sm:order-1 p-8 sm:p-10 sm:w-1/2 flex flex-col justify-between gap-10">
          <div>
            <p className="font-header text-2xl sm:text-3xl mb-8 leading-snug text-secondary-dark">
              {lang ? (
                <span>
                  New Vision
                  <br />
                  International Group
                  <br />
                  Headquarter
                </span>
              ) : (
                <span>
                  新愿景国际
                  <br />
                  集团总部
                </span>
              )}
            </p>
            <div className="flex flex-col gap-3 font-text text-sm leading-7 text-secondary-dark">
              <p>
                16 Berkeley Street, Mayfair
                <br />
                London, W1J 8DZ, United Kingdom
              </p>
              <p>+44 (0)20 3907 4025</p>
              <p>
                <a
                  className="hover:text-main transition-colors"
                  href="mailto:info@newvgroup.com"
                >
                  info@newvgroup.com
                </a>
              </p>
            </div>
          </div>

          <div className="flex gap-4">
            <div>
              <img
                className="w-20 rounded-sm"
                src={qrChat}
                alt={lang ? "QR code for wechat" : "微信二维码"}
              />
              <p className="text-center text-[10px] tracking-widest uppercase mt-2 text-secondary-dark/60">
                Chat with Us
              </p>
            </div>
            <div>
              <img
                className="w-20 rounded-sm"
                src={qrFollow}
                alt={lang ? "QR code for wechat" : "微信二维码"}
              />
              <p className="text-center text-[10px] tracking-widest uppercase mt-2 text-secondary-dark/60">
                Follow Us
              </p>
            </div>
          </div>
        </div>
        <img
          className="w-full sm:w-1/2 object-cover order-1 sm:order-2 min-h-[280px]"
          src={imgOffice}
          alt=""
        />
      </div>
      <div className="header-border-t-flex">
        <h2>{lang ? "Contact" : "联系"}</h2>
        <ContactForm />
      </div>
      <FAQSection lang={lang} />
      <Publication />
      <NewsHighlightSection />
    </div>
  );
}

const faqData = [
  {
    question: {
      en: "What is New Vision International Group (NVIG)?",
      cn: "什么是新愿景国际集团（NVIG）？",
    },
    answer: {
      en: "New Vision International Group (NVIG) is a London-based global asset allocation platform. Founded by Cambridge University graduates, we provide comprehensive, customised solutions for businesses, high-net-worth individuals, and families seeking international growth and diversification.",
      cn: "新愿景国际集团（NVIG）是一个总部位于英国伦敦的全球资产配置专业平台。由剑桥大学毕业生创立，为有全球化综合发展需求的企业、高净值人士和家族提供一站式定制服务。",
    },
  },
  {
    question: {
      en: "What services does NVIG offer?",
      cn: "NVIG提供哪些服务？",
    },
    answer: {
      en: "Our business covers seven key segments: Family Office (wealth management & legacy planning), Real Estate (property investment & advisory), Technology Investment (UK innovation & global capital), Art & Culture (art advisory & cultural exchange), Think Tank (global wealth management insights), Private Club (V Club — exclusive lifestyle & networking), and the Cambridge Foundation (academic excellence & philanthropy).",
      cn: "集团业务涵盖七大板块：家族办公室（财富管理与传承规划）、地产置业（房产投资与咨询）、科技投资（英国创新与全球资本）、艺术文化（艺术咨询与文化交流）、智库交流（全球资产配置洞察）、私人俱乐部（V Club — 高端生活方式与社交）、以及剑桥基金会（学术卓越与慈善事业）。",
    },
  },
  {
    question: {
      en: "Where is NVIG headquartered?",
      cn: "NVIG总部在哪里？",
    },
    answer: {
      en: "Our headquarters is located at 16 Berkeley Street, Mayfair, London, W1J 8DZ, United Kingdom. We are situated in the heart of Mayfair, one of London's most prestigious districts.",
      cn: "我们的总部位于英国伦敦梅菲尔区伯克利街16号（16 Berkeley Street, Mayfair, London, W1J 8DZ）。我们坐落于伦敦最负盛名的梅菲尔区核心地段。",
    },
  },
  {
    question: {
      en: "How can I schedule a consultation?",
      cn: "如何预约咨询？",
    },
    answer: {
      en: "You can reach us by phone at +44 (0)20 3907 4025, email us at info@newvgroup.com, or use the contact form on this page. You can also scan our WeChat QR codes to chat with us directly. Our team will respond to your enquiry promptly.",
      cn: "您可以通过以下方式联系我们：致电 +44 (0)20 3907 4025，发送邮件至 info@newvgroup.com，或使用本页面的联系表单。您也可以扫描我们的微信二维码直接与我们沟通。我们的团队将及时回复您的咨询。",
    },
  },
  {
    question: {
      en: "What qualifications and certifications does NVIG hold?",
      cn: "NVIG持有哪些资质和认证？",
    },
    answer: {
      en: "NVIG holds multiple professional certifications, including IAA (Immigration Advice Authority) for regulated immigration advice, ARLA Propertymark for professional property standards, and CIOL (Chartered Institute of Linguists) membership for certified language services. All business activities are conducted under the supervision of relevant UK regulatory bodies.",
      cn: "NVIG持有多项专业资质认证，包括IAA（英国移民咨询署特许法律机构）提供受监管的移民咨询、ARLA Propertymark（英国房产监管认证咨询机构）确保房产行业专业标准、以及CIOL（英国皇家翻译协会认证会员）提供认证语言服务。所有业务均在英国相关权威机构监管下开展。",
    },
  },
  {
    question: {
      en: "What is the Family Office service?",
      cn: "什么是家族办公室服务？",
    },
    answer: {
      en: "Our Family Office acts as your 'Wealth Steward,' providing ultra-high-net-worth families with bespoke services including global asset allocation, wealth inheritance, trust structuring, tax planning, private banking, identity planning, children's education, and philanthropy. Our expert team spans private banking, legal, tax, asset management, real estate, insurance, accounting, and education.",
      cn: "我们的家族办公室担任您的\u201C财富管家\u201D，为超高净值家族提供一站式定制服务，包括全球资产配置、财富传承、信托架构、税务筹划、私人银行、身份规划、子女教育和慈善等。我们的专家团队涵盖私人银行、法律、税务、资产管理、地产、保险、会计和教育等领域。",
    },
  },
  {
    question: {
      en: "Does NVIG assist with UK property investment?",
      cn: "NVIG是否协助英国房产投资？",
    },
    answer: {
      en: "Yes. New Vision Real Estate, certified by ARLA Propertymark, specialises in premium property investments for high-net-worth individuals. We offer property selection, mortgage assistance, legal coordination, tax planning, interior design, leasing & management, and school placement services — providing a complete end-to-end property investment experience.",
      cn: "是的。新愿景地产受ARLA Propertymark认证，专注于为高净值人群提供优质地产投资项目。我们提供房产甄选、银行贷款、律师对接、税务规划、家装设计、租赁管理和子女入学等全方位服务——提供完整的端到端房产投资体验。",
    },
  },
  {
    question: {
      en: "What languages does NVIG support?",
      cn: "NVIG支持哪些语言？",
    },
    answer: {
      en: "Our team is fully bilingual in English and Chinese (Mandarin). As a CIOL (Chartered Institute of Linguists) certified member, we ensure accurate and professional communication. Our deep understanding of both Western and Chinese cultural contexts allows us to bridge the gap for clients seamlessly.",
      cn: "我们的团队精通中英双语。作为CIOL（英国皇家翻译协会）认证会员，我们确保准确、专业的沟通。我们对中西方文化的深刻理解，能够为客户搭建无缝沟通的桥梁。",
    },
  },
];

function FAQSection({ lang }: { lang: boolean }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="mt-24 mb-16">
      <h2 className="header-border-t">
        {lang ? "Frequently Asked Questions" : "常见问题"}
      </h2>
      <div className="mt-10">
        {faqData.map((item, index) => {
          const isOpen = openIndex === index;
          return (
            <div
              key={index}
              className={`border-b border-secondary-dark/15 transition-colors ${
                isOpen ? "border-secondary-dark/40" : ""
              }`}
            >
              <button
                onClick={() => toggle(index)}
                className="w-full flex justify-between items-center py-6 text-left gap-6 group"
              >
                <span className="font-poster text-main text-base shrink-0 w-8 tracking-widest">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <span className="flex-1 font-header text-lg sm:text-xl leading-snug text-secondary-dark group-hover:text-main transition-colors">
                  {lang ? item.question.en : item.question.cn}
                </span>
                <motion.span
                  animate={{ rotate: isOpen ? 45 : 0 }}
                  transition={{ duration: 0.2 }}
                  className="text-2xl font-light flex-shrink-0 leading-none text-secondary-dark/70"
                >
                  +
                </motion.span>
              </button>
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <p className="pb-6 pl-14 text-[14.5px] text-secondary-dark/80 leading-8">
                      {lang ? item.answer.en : item.answer.cn}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
}

type FormData = {
  name: string;
  email: string;
  subject: string;
  content: string;
};

const ContactForm: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    subject: "",
    content: "",
  });
  const [response, setResponse] = useState("");
  const [disableButton, setDisableButton] = useState(false);
  const lang = useSelector(getLang);
  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    console.log({ name, value });
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (
      !formData.name.trim() ||
      !formData.email.trim() ||
      !formData.content.trim()
    ) {
      setResponse(lang ? "Please fill all * fields." : "请填写所有 * 字段。");
      return;
    }
    if (!formData.email.trim().replace(" ", "").includes("@")) {
      setResponse(
        lang
          ? "Please input a valid email address."
          : "请输入有效的电子邮件地址。",
      );
      return;
    }
    setDisableButton(true);
    const response = await sendEmail({
      name: formData.name,
      email: formData.email,
      content: formData.content,
      subject: formData.subject,
    });
    if (response === 200) {
      setResponse(lang ? "Email sent successfully" : "电子邮件发送成功");
      setFormData({
        name: "",
        email: "",
        subject: "",
        content: "",
      });
    } else {
      setResponse(lang ? "Email failed" : "电子邮件发送失败");
    }
    setDisableButton(false);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-6 mt-16 sm:mt-0 w-full"
    >
      <div className="flex gap-6">
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder={lang ? "Name*" : "姓名*"}
          className="field-input"
        />
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder={lang ? "Email*" : "邮箱*"}
          className="field-input"
        />
      </div>
      <input
        type="text"
        id="subject"
        name="subject"
        value={formData.subject}
        onChange={handleChange}
        placeholder={lang ? "Subject" : "主题"}
        className="field-input"
      />
      <textarea
        name="content"
        id="content"
        value={formData.content}
        onChange={handleChange}
        placeholder={lang ? "Message*" : "信息*"}
        className="field-input h-48 resize-none"
      />
      <button
        type="submit"
        disabled={disableButton}
        className={`btn-solid mt-4 self-start ${
          disableButton ? "opacity-70" : ""
        }`}
      >
        {disableButton ? <Spinner /> : lang ? "Send Message" : "发送信息"}
      </button>
      {response && (
        <p className="text-sm text-secondary-dark/70 mt-2">{response}</p>
      )}
    </form>
  );
};
